import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, Form, Input, Link, Popconfirm, Space, Table } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import useTable from '@/hooks/useTable';
import { fetchPermissions } from '@/slices/dictSlice';
import { deletePermission, getNamespaceGrantList } from '@/services/namespaceService';
import Secret from '@/components/SecretDisplay';
import PermWrapper from '@/components/PermWrapper';
import DisableWrapper from '@/components/DisableWrapper';
import { deal70101 } from '@/utils/common';
import Modal from './Modal';

const { FormItem } = Form;

function PermissionPanel({ clusterName, namespace, fetchPermissions }) {
  const [visible, setVisible] = useState(false);
  const [update, setUpdate] = useState(false);

  const columns = [
    {
      colKey: 'roleName',
      title: 'Role',
      width: 150,
    },
    {
      colKey: 'roleKey',
      title: '密钥',
      cell: ({ row }) => <Secret>{row.roleKey}</Secret>,
      width: 200,
    },
    {
      colKey: 'permissions',
      title: '操作权限',
      cell: ({ row }) => (
        <Space size={[4, 2]} breakLine>
          {row?.permissions?.map((item) => (
            <Tag key={item}>{item}</Tag>
          ))}
        </Space>
      ),
    },
    {
      colKey: 'updateTime',
      title: '更新时间',
      cell: ({ row }) => dayjs(row.updateTime).format('YYYY-MM-DD HH:mm:ss'),
      width: 180,
    },
    {
      colKey: 'option',
      title: '操作',
      align: 'center',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='pulsar:namespace:list:roleUpdate'>
            <Link theme='primary' onClick={() => openUpdateModal(row)} hover='color'>
              编辑
            </Link>
          </PermWrapper>
          <PermWrapper resource='pulsar:namespace:list:roleDelete'>
            <Popconfirm theme='danger' content='确定删除？' onConfirm={() => handleDel(row)}>
              <DisableWrapper>
                <Link theme='danger' hover='color'>
                  移除
                </Link>
              </DisableWrapper>
            </Popconfirm>
          </PermWrapper>
        </Space>
      ),
      width: 120,
    },
  ];

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getNamespaceGrantList({ ...params, clusterName, namespace });
    setData(data);
  });

  const { header, config, setData, doRefresh, doSearch } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <FormItem name='role'>
        <Input placeholder='请输入Role' clearable />
      </FormItem>
    ),
    operators: (
      <PermWrapper resource='pulsar:namespace:list:roleAdd'>
        <Button
          theme='primary'
          onClick={() => {
            setVisible(true);
            setUpdate(false);
          }}
        >
          添加权限
        </Button>
      </PermWrapper>
    ),
  });

  useEffect(() => {
    if (clusterName) {
      doSearch();
      fetchPermissions();
    }
  }, [clusterName, doSearch, fetchPermissions]);

  const openUpdateModal = (row) => {
    setUpdate({
      clusterName,
      role: row.roleName,
      permissions: row.permissions,
    });
    setVisible(true);
  };

  const handleDel = async (row) => {
    const { meta } = await deletePermission({
      clusterName,
      role: row.roleName,
      namespace,
    });
    // 主集群创建的资源已在灾备集群存在
    if (meta.code === 70101) {
      deal70101(meta.publicMessage, () => handleDel(row), {
        url: '/cluster/namespace/list',
        namespace,
      });

      return;
    }
    doRefresh();
  };

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      // 刷新
      doRefresh();
    }
  };

  return (
    <Root>
      <H3>授权</H3>
      {header}
      <Table {...config} />
      <Modal
        clusterName={clusterName}
        visible={visible}
        update={update}
        namespace={namespace}
        handleClose={handleClose}
      />
    </Root>
  );
}

export default connect(null, {
  fetchPermissions,
})(PermissionPanel);

const Root = styled.div`
  padding: 0 6px;
`;

const H3 = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  color: var(--td-font-gray-1);
  padding: 20px 0 16px;
`;

const Tag = styled.div`
  background: rgba(102, 136, 255, 0.12);
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 12px;
  color: #6688ff;
  display: flex;
  align-items: center;
  justify-content: center;
`;
